import dayjs from 'dayjs'
import { CircleAlert, TriangleAlert } from '@/components/icons'
import { DocumentSubType, documentSubTypeLabels } from '@@api/shared'
import { getDocumentIcon, getDocumentExpiryStatus } from './DocumentListCard'

interface DocumentsGroupCardDoc {
  id: string
  title: string
  subType?: DocumentSubType | null
  expiryDate?: Date | string | null
}

interface DocumentsGroupCardProps {
  name: string
  documents: DocumentsGroupCardDoc[]
  onDocumentClick: (documentId: string) => void
  /** Icon per sub-type; defaults to the crew/aircraft set. */
  getIcon?: (subType: DocumentSubType) => typeof CircleAlert
}

/**
 * A person's documents grouped under one card headed by their name, with a
 * worst-of status pill (any expired > any expiring) and per-row expiry pills.
 * Used by the add-crew and add-passenger wizards' Documents steps.
 */
export function DocumentsGroupCard({
  name,
  documents,
  onDocumentClick,
  getIcon = getDocumentIcon,
}: DocumentsGroupCardProps) {
  const statuses = documents.map((doc) =>
    doc.expiryDate ? getDocumentExpiryStatus(doc.expiryDate) : 'valid'
  )
  const worstStatus = statuses.includes('expired')
    ? 'expired'
    : statuses.includes('expiring')
      ? 'expiring'
      : 'valid'

  return (
    <div className="border border-border-L2 rounded-2xl px-4 py-2 flex flex-col">
      <div className="flex items-center justify-between py-1">
        <p className="text-base font-semibold text-content-primary-bold">{name}</p>
        {worstStatus === 'expired' && (
          <span className="flex items-center gap-1 text-xs font-medium text-content-negative-bold bg-background-negative-subtle px-2 py-0.5 rounded-full">
            <TriangleAlert className="size-4" />
          </span>
        )}
        {worstStatus === 'expiring' && (
          <span className="flex items-center gap-1 text-xs font-medium text-background-notice-bold bg-background-notice-subtle px-2 py-0.5 rounded-full">
            <CircleAlert className="size-4" />
          </span>
        )}
      </div>
      {documents.map((doc) => {
        const DocumentIcon = getIcon(doc.subType ?? DocumentSubType.Other)
        const status = doc.expiryDate ? getDocumentExpiryStatus(doc.expiryDate) : null
        return (
          <div key={doc.id}>
            <hr className="border-border-L1" />
            <button
              type="button"
              onClick={() => onDocumentClick(doc.id)}
              className="w-full flex items-center justify-between gap-2 py-3 text-left"
            >
              <span className="flex min-w-0 items-center gap-2">
                <DocumentIcon className="size-4 shrink-0 text-content-brand-bold" />
                <span className="truncate text-sm font-medium text-content-primary-bold">
                  {doc.subType ? documentSubTypeLabels[doc.subType] : doc.title}
                </span>
              </span>
              {status === 'expired' ? (
                <span className="shrink-0 text-xs font-medium text-content-negative-bold bg-background-negative-subtle px-2 py-0.5 rounded-full tracking-wide">
                  Expired {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                </span>
              ) : status === 'expiring' ? (
                <span className="shrink-0 text-xs font-medium text-background-notice-bold bg-background-notice-subtle px-2 py-0.5 rounded-full tracking-wide">
                  Expires {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                </span>
              ) : (
                <span className="shrink-0 text-xs font-medium text-content-primary-subtle">
                  {status === 'valid'
                    ? `Expires ${dayjs(doc.expiryDate).format('MMM D, YYYY')}`
                    : 'Expiry date not set'}
                </span>
              )}
            </button>
          </div>
        )
      })}
    </div>
  )
}
